import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Stack,
  IconButton,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import StyledButton from '../../common/StyledButton';
import api from '../../../services/api';

const MAX_PHOTOS = 10;
const MAX_MB = 15;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
];
// iOS Safari frequently reports HEIC/HEIF with an empty MIME type, so we also
// accept by file extension as a fallback for type validation.
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif'];

const hasAcceptedExtension = (name = '') => {
  const lower = name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const isAcceptedFile = (file) =>
  ACCEPTED_TYPES.includes(file.type) || hasAcceptedExtension(file.name);

// Photos are served from /uploads (a sibling of /api), so peel /api off the
// configured base URL to build absolute thumbnail URLs the browser can hit.
const photoOrigin = (() => {
  const base = api.defaults.baseURL || '';
  return base.replace(/\/api\/?$/, '');
})();

const photoUrl = (photo) => {
  if (!photo) return '';
  if (photo.url?.startsWith('http')) return photo.url;
  return `${photoOrigin}${photo.url}`;
};

const ProductPhotosStep = ({ onNext, onBack }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const fileInputRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const { data } = await api.get('/vendor/product-photos');
      setPhotos(Array.isArray(data?.photos) ? data.photos : []);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "We couldn't load your photos. Refresh the page to try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const remainingSlots = Math.max(0, MAX_PHOTOS - photos.length);

  const handleFiles = async (fileList) => {
    setError('');
    setNotice('');
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const invalidType = incoming.find((f) => !isAcceptedFile(f));
    if (invalidType) {
      setError(
        `"${invalidType.name}" is not a supported image type. Use JPEG, PNG, WebP, GIF, or HEIC.`
      );
      return;
    }
    const tooLarge = incoming.find((f) => f.size > MAX_BYTES);
    if (tooLarge) {
      setError(`"${tooLarge.name}" is larger than ${MAX_MB} MB. Please choose a smaller file.`);
      return;
    }
    if (incoming.length > remainingSlots) {
      setNotice(
        `Only the first ${remainingSlots} photo${remainingSlots === 1 ? '' : 's'} will be uploaded ` +
          `(${MAX_PHOTOS} max).`
      );
    }
    const toSend = incoming.slice(0, remainingSlots);
    if (toSend.length === 0) return;

    const formData = new FormData();
    toSend.forEach((file) => formData.append('photos', file));

    setUploading(true);
    try {
      const { data } = await api.post('/vendor/product-photos', formData, {
        headers: { 'Content-Type': undefined },
      });
      setPhotos(Array.isArray(data?.photos) ? data.photos : []);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          `We couldn't upload your photos. Make sure each file is under ${MAX_MB} MB and is a JPEG, PNG, WebP, GIF, or HEIC, then try again.`
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (photoId) => {
    setError('');
    setNotice('');
    try {
      await api.delete(`/vendor/product-photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "We couldn't delete that photo. Refresh the page and try again."
      );
    }
  };

  const handleContinue = async () => {
    setAdvancing(true);
    try {
      await onNext({ productPhotoCount: photos.length });
    } finally {
      setAdvancing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  const preventDefault = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const dropAreaDisabled = uploading || remainingSlots === 0;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#fafbfc' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <PhotoLibraryIcon color="primary" />
          <Typography variant="h6">Product Photos (Optional)</Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
          Upload up to {MAX_PHOTOS} photos of your current products so our review team can get a feel
          for what you'll be selling. JPEG, PNG, WebP, GIF, or HEIC (including iPhone photos), up to{' '}
          {MAX_MB} MB each. You can skip this step and come back to it later if you'd like.
        </Typography>

        <Paper
          variant="outlined"
          onClick={() => !dropAreaDisabled && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={preventDefault}
          onDragEnter={preventDefault}
          sx={{
            p: 4,
            mb: 2,
            textAlign: 'center',
            cursor: dropAreaDisabled ? 'not-allowed' : 'pointer',
            borderStyle: 'dashed',
            borderColor: dropAreaDisabled ? 'divider' : 'primary.light',
            backgroundColor: dropAreaDisabled ? '#f5f5f5' : '#fff',
            transition: 'border-color 0.15s ease',
            '&:hover': dropAreaDisabled ? {} : { borderColor: 'primary.main' },
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {remainingSlots === 0
              ? `You've reached the ${MAX_PHOTOS}-photo limit`
              : 'Drag and drop photos here, or click to choose files'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {photos.length} of {MAX_PHOTOS} used • JPEG / PNG / WebP / GIF / HEIC • {MAX_MB} MB max each
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
            style={{ display: 'none' }}
            onChange={(e) => handleFiles(e.target.files)}
          />
        </Paper>

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary">
              Uploading…
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : photos.length > 0 ? (
          <ImageList cols={3} gap={12} sx={{ m: 0 }}>
            {photos.map((photo) => (
              <ImageListItem
                key={photo.id}
                sx={{ overflow: 'hidden', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              >
                <img
                  src={photoUrl(photo)}
                  alt={photo.original_name || 'Product photo'}
                  loading="lazy"
                  style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                />
                <ImageListItemBar
                  title={photo.original_name || 'Product photo'}
                  position="bottom"
                  actionIcon={
                    <IconButton
                      sx={{ color: '#fff' }}
                      aria-label={`Delete ${photo.original_name || 'photo'}`}
                      onClick={() => handleDelete(photo.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No photos uploaded yet.
          </Typography>
        )}
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <StyledButton onClick={onBack} variant="outlined" disabled={uploading || advancing}>
          Back
        </StyledButton>
        <StyledButton
          onClick={handleContinue}
          variant="contained"
          disabled={uploading || advancing}
        >
          {advancing ? 'Saving…' : photos.length === 0 ? 'Skip for Now' : 'Continue'}
        </StyledButton>
      </Box>
    </Box>
  );
};

export default ProductPhotosStep;
